import React, { useEffect, useRef, useState } from "react";
import { Col, Row } from "antd";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import CustomButton from "../../../components/global/Button/CustomButton";
import CustomInput from "../../../components/global/CustomInput/CustomInput";
import LottieIcon from "../../../components/shared/lottieIcon/LottieIcon";
import searchIconsvg from "../../../assets/icons/Search-Icon.svg";
import backButton from "../../../assets/back-btn.svg";
import AdminHOC from "../../../components/shared/HOC/AdminHOC";
import CommonTable from "../../../components/shared/table/Table";
import PageLoader from "../../../components/global/Loader/PageLoader";
import usePagination from "../../../assets/hooks/PaginationHooks/usePagination";
import useGroupUserKpiReport from "../../../hooks/useGroupUserKpiReport";
import { GroupUserReportColumns } from "../../../assets/FormFeilds/GroupUserReportColumns";
import { downloadGroupUserKpiReport } from "../../../service/ReportingService";
import { showErrorMessage, showSuccessMessage } from "../../../components/shared/pagination/messages";

const GroupUsersReport = () => {
  const { id: groupId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const tableRef = useRef(null);

  const [searchText, setSearchText] = useState("");
  const [isInputHovered, setIsInputHovered] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showLoader, setShowLoader] = useState(false);

  const {
    currentPage,
    pageSize,
    setCurrentPage,
    totalElements,
    setTotalElements,
  } = usePagination();

  const { data, groupName, fetchGroupUserKpiReport } = useGroupUserKpiReport();

  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await fetchGroupUserKpiReport(groupId, currentPage, pageSize, searchText);
      if (response) {
        setTotalElements(response.data?.totalElements || 0);
      }
    } catch (err) {
      console.error("Fetch Error:", err);
      setTotalElements(0);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (value) => {
    setSearchText(value.toLowerCase());
    setCurrentPage(1);
  };

  const handleTableChange = (pagination) => {
    if (pagination) {
      if (pagination.pageSize !== pageSize) {
        setCurrentPage(1);
      } else {
        setCurrentPage(pagination.current);
      }
      const scrollableNode = tableRef.current?.querySelector(".ant-table-body");
      if (scrollableNode) {
        scrollableNode.scrollTo({ top: 0, behavior: "auto" });
      }
    }
  };

  useEffect(() => {
    const debounce = setTimeout(() => {
      const query = searchText?.trim() || "";
      fetchData(currentPage, pageSize, query);
    }, 500);
    return () => clearTimeout(debounce);
  }, [currentPage, pageSize, searchText, groupId]);

  const downloadReport = async () => {
    setShowLoader(true);
    try {
      await downloadGroupUserKpiReport(groupId, searchText);
      showSuccessMessage("Report Downloaded Successfully!");
    } catch (error) {
      showErrorMessage(error?.message || "Failed to Download Report");
    } finally {
      setShowLoader(false);
    }
  };

  return (
    <>
      <PageLoader visible={showLoader} />
      <section className="w-[100%] max-w-full h-[95vh] p-6 flex flex-col border border-[#E8E8E8] rounded-[10px] shadow-[0px_0px_6px_0px_rgba(0,0,0,0.05)]">
        <header className="flex flex-col gap-6 mb-2">
          <div className="flex justify-between items-center h-[50px]">
            <h1 className="text-[#E74860] text-[1.75rem] font-normal">
              {groupName || location.state?.name || "Group"}  User Report
            </h1>
            <div
              className="flex gap-2 justify-center cursor-pointer"
              onClick={() => navigate(-1)}
            >
              <img alt="back-btn" src={backButton} />
              <span>Back</span>
            </div>
          </div>
        </header>

        <Row span={2}>
          <Col span={6} />
          <Col span={18}>
            <div style={{ display: "flex", gap: "10px", justifyContent: "flex-end", paddingBottom: "10px"}}>
              <div
                onMouseEnter={() => setIsInputHovered(true)}
                onMouseLeave={() => setIsInputHovered(false)}
              >
                <CustomInput
                  icon={
                    isInputHovered ? (
                      <LottieIcon name="searchIcon" size={24} shouldPlay={true} />
                    ) : (
                      <img src={searchIconsvg} alt="search" className="w-6 h-6" />
                    )
                  }
                  className="!w-[250px]"
                  onChange={(e) => handleSearch(e.target.value)}
                  placeholder="Search employee"
                  allowClear
                />
              </div>
              <CustomButton onClick={downloadReport}>
                Export
              </CustomButton>
            </div>
          </Col>
        </Row>

        <div ref={tableRef}>
          <CommonTable
            data={data}
            columns={GroupUserReportColumns()}
            showLoader={loading}
            className="hide-scrollbar top-table-radius"
            scroll={{ y: "60vh" }}
            pagination={{
              current: currentPage,
              pageSize: pageSize,
              total: totalElements,
              showSizeChanger: true,
            }}
            onChange={handleTableChange}
            rowKey={(record) => record.userId}
            serverSide={true}
          />
        </div>
      </section>
    </>
  );
};

export default AdminHOC(GroupUsersReport);